import { useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Play,
  Settings,
  RotateCcw,
  X,
  Pause,
  Plus,
  Edit,
  Save,
  ChevronDown,
  ChevronUp,
  Power,
  Users,
  Copy,
  BadgeCheck,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Select, SelectContent, SelectTrigger, SelectItem, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useToast } from "../hooks/useToast";
import { apiRequest } from "../components/lib/queryClient";
import { useAuth } from "../components/features/AuthContext";
import { ApiResponse } from "../components/lib/utils";
import { FilterApiSchema } from "./manual-testing";
import { OrderSettingsFormData } from "./order-settings";
import { Bucket } from "./bucket-management";
import FilterConfigurationForm from "../components/FilterConfigFormAutoTrade";

/**
 * AutoTrade is the single strategy hub.
 *
 * A "strategy" here is a master strategy: an admin creates it, activates it and
 * edits its filter configurations. Any other user browses the same list and
 * subscribes to a strategy, controlling how much capital it may use and whether
 * it is currently enabled for them. Master strategies are executed by the shared
 * trading cycle for every active, enabled subscription, so there is no separate
 * per-user strategy concept.
 *
 * Master strategies and copy-trading subscriptions are crypto-backend features,
 * so this page always talks to the crypto backend regardless of the global
 * trading-mode toggle.
 */

const CRYPTO = "crypto";

// ─── Schemas ────────────────────────────────────────────────────────────────

const filterConfigSchema = z.object({
  filterId: z.string().min(1, "Filter is required"),
  bucketIds: z.array(z.string()).min(1, "At least one bucket is required"),
  timeInMinutes: z.coerce
    .number({ invalid_type_error: "Required" })
    .int("Whole minutes")
    .min(5, "Minimum 5 minutes")
    .max(2880, "Maximum 2880 minutes"),
  orderSettingId: z.string().min(1, "Order setting is required"),
  exitFilterIds: z.array(z.string()).optional(),
});

export const strategySchema = z.object({
  _id: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  filterConfigs: z.array(filterConfigSchema).min(1, "At least one filter configuration is required"),
  refreshInterval: z.coerce
    .number({ invalid_type_error: "Required" })
    .int("Whole seconds")
    .min(5, "Minimum 5 seconds")
    .max(86400, "Maximum 86400 seconds"),
  trailingEnabled: z.boolean(),
  autoCutoffEnabled: z.boolean(),
  isActive: z.boolean(),
  maxFollowers: z.string().optional(),
  minCapital: z.string().optional(),
});

export type FilterConfigFormData = z.infer<typeof filterConfigSchema>;
export type StrategyFormData = z.infer<typeof strategySchema>;

const subscribeSchema = z.object({
  capitalAllocation: z.coerce
    .number({ invalid_type_error: "Required" })
    .min(1, "Minimum 1%")
    .max(100, "Maximum 100%"),
  maxPositionSize: z.string().optional(),
  stopLossOverride: z.string().optional(),
  trailingStopOverride: z.string().optional(),
});

const subscriptionUpdateSchema = z.object({
  isEnabled: z.boolean(),
  capitalAllocation: z.coerce
    .number({ invalid_type_error: "Required" })
    .min(1, "Minimum 1%")
    .max(100, "Maximum 100%"),
  maxPositionSize: z.string().optional(),
  stopLossOverride: z.string().optional(),
  trailingStopOverride: z.string().optional(),
});

type SubscribeFormValues = z.infer<typeof subscribeSchema>;
type SubscriptionUpdateValues = z.infer<typeof subscriptionUpdateSchema>;

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MasterStrategy {
  _id: string;
  name: string;
  description?: string | null;
  filterConfigs: FilterConfigFormData[];
  autoCutoffEnabled: boolean;
  refreshInterval: number;
  trailingEnabled: boolean;
  category: string;
  isActive: boolean;
  maxFollowers?: number | null;
  minCapital?: number | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  subscriber_count?: number;
}

export interface CopyTradingSubscription {
  _id: string;
  userId: string;
  masterStrategyId: string;
  isEnabled: boolean;
  capitalAllocation: number;
  maxPositionSize?: number | null;
  stopLossOverride?: number | null;
  trailingStopOverride?: number | null;
  isActive: boolean;
  totalTrades: number;
  totalPnl: number;
  startedAt?: string;
  lastTradeAt?: string | null;
  master_strategy_name?: string;
  master_strategy_active?: boolean;
}

interface BotDetail {
  bot_id: string;
  name: string;
  strategy_type: string;
  refresh_interval: number;
}

interface AutoTradeRunningStatusResponse {
  status: "success";
  user_id: string;
  running_bot_ids: string[];
  details: BotDetail[];
}

interface CutoffStatusResponse {
  user_id: string;
  cutoff_running: boolean;
}

const defaultStrategyValues: StrategyFormData = {
  name: "",
  description: "",
  filterConfigs: [
    { filterId: "", bucketIds: [], timeInMinutes: 60, orderSettingId: "", exitFilterIds: [] },
  ],
  refreshInterval: 60,
  trailingEnabled: false,
  autoCutoffEnabled: false,
  isActive: true,
  maxFollowers: "",
  minCapital: "",
};

const emptySubscribe: SubscribeFormValues = {
  capitalAllocation: 100,
  maxPositionSize: "",
  stopLossOverride: "",
  trailingStopOverride: "",
};

const toFormValues = (strategy?: MasterStrategy | null): StrategyFormData => {
  if (!strategy) return defaultStrategyValues;
  return {
    _id: strategy._id,
    name: strategy.name,
    description: strategy.description ?? "",
    filterConfigs: (strategy.filterConfigs ?? []).map((config) => ({
      filterId: config.filterId,
      bucketIds: config.bucketIds ?? [],
      timeInMinutes: config.timeInMinutes,
      orderSettingId: config.orderSettingId,
      exitFilterIds: config.exitFilterIds ?? [],
    })),
    refreshInterval: strategy.refreshInterval,
    trailingEnabled: !!strategy.trailingEnabled,
    autoCutoffEnabled: !!strategy.autoCutoffEnabled,
    isActive: !!strategy.isActive,
    maxFollowers: strategy.maxFollowers == null ? "" : String(strategy.maxFollowers),
    minCapital: strategy.minCapital == null ? "" : String(strategy.minCapital),
  };
};

const toPayload = (values: StrategyFormData) => ({
  name: values.name,
  description: values.description || null,
  filterConfigs: values.filterConfigs,
  autoCutoffEnabled: values.autoCutoffEnabled,
  refreshInterval: values.refreshInterval,
  trailingEnabled: values.trailingEnabled,
  category: CRYPTO,
  isActive: values.isActive,
  maxFollowers: values.maxFollowers?.trim() ? Number(values.maxFollowers) : null,
  minCapital: values.minCapital?.trim() ? Number(values.minCapital) : null,
});

const toSubscriptionPayload = (values: {
  capitalAllocation: number;
  maxPositionSize?: string;
  stopLossOverride?: string;
  trailingStopOverride?: string;
}) => ({
  capitalAllocation: values.capitalAllocation,
  maxPositionSize: values.maxPositionSize?.trim() ? Number(values.maxPositionSize) : undefined,
  stopLossOverride: values.stopLossOverride?.trim() ? Number(values.stopLossOverride) : undefined,
  trailingStopOverride: values.trailingStopOverride?.trim()
    ? Number(values.trailingStopOverride)
    : undefined,
});

const percentToForm = (value?: number | null) => (value == null ? "" : String(value));

// ─── Component ──────────────────────────────────────────────────────────────

export default function AutoTrade() {
  const { user, currentTradingMode } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isAdmin = user?.role === "admin";

  const [activeTab, setActiveTab] = useState<"strategies" | "subscriptions">("strategies");
  const [showCreate, setShowCreate] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<MasterStrategy | null>(null);
  const [subscribeTarget, setSubscribeTarget] = useState<MasterStrategy | null>(null);
  const [editSubscription, setEditSubscription] = useState<CopyTradingSubscription | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const strategyForm = useForm<StrategyFormData>({
    resolver: zodResolver(strategySchema),
    defaultValues: defaultStrategyValues,
  });
  const { fields, append, remove } = useFieldArray({
    control: strategyForm.control,
    name: "filterConfigs",
  });

  const subscribeForm = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeSchema),
    defaultValues: emptySubscribe,
  });

  const subscriptionForm = useForm<SubscriptionUpdateValues>({
    resolver: zodResolver(subscriptionUpdateSchema),
    defaultValues: {
      isEnabled: true,
      capitalAllocation: 100,
      maxPositionSize: "",
      stopLossOverride: "",
      trailingStopOverride: "",
    },
  });

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: strategies, isLoading: strategiesLoading } = useQuery<MasterStrategy[]>({
    queryKey: ["api/master-strategies", "auto-trade"],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (isAdmin) params.set("active_only", "false");
      const query = params.toString();
      const res = await apiRequest<ApiResponse<MasterStrategy[]>>(
        "GET",
        `api/master-strategies${query ? `?${query}` : ""}`,
        undefined,
        undefined,
        "crypto"
      );
      return res.data ?? [];
    },
  });

  const { data: subscriptions, isLoading: subscriptionsLoading } = useQuery<
    CopyTradingSubscription[]
  >({
    queryKey: ["api/copy-trading/subscriptions"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<CopyTradingSubscription[]>>(
        "GET",
        "api/copy-trading/subscriptions",
        undefined,
        undefined,
        "crypto"
      );
      return res.data ?? [];
    },
  });

  const { data: filters } = useQuery<FilterApiSchema[]>({
    queryKey: ["api/filters", CRYPTO, "auto-trade"],
    queryFn: async () => {
      const params = new URLSearchParams({ category: CRYPTO, code: "0" });
      const res = await apiRequest<ApiResponse<FilterApiSchema[]>>(
        "GET",
        `api/filters?${params.toString()}`
      );
      return res.data ?? [];
    },
  });

  const { data: buckets } = useQuery<Bucket[]>({
    queryKey: ["api/buckets", user?.userId, user?.role, CRYPTO],
    queryFn: async () => {
      if (!user?.userId || !user?.role) return [];
      const params = new URLSearchParams({
        userId: user.userId,
        role: user.role,
        category: CRYPTO,
      });
      const res = await apiRequest<ApiResponse<Bucket[]>>(
        "GET",
        `api/buckets?${params.toString()}`
      );
      return res.data ?? [];
    },
    enabled: !!user?.userId && !!user?.role,
  });

  const { data: orderSettings } = useQuery<OrderSettingsFormData[]>({
    queryKey: ["api/order-settings", user?.userId, user?.role, CRYPTO],
    queryFn: async () => {
      if (!user?.userId || !user?.role) return [];
      const params = new URLSearchParams({
        userId: user.userId,
        role: user.role,
        category: CRYPTO,
      });
      const res = await apiRequest<ApiResponse<OrderSettingsFormData[]>>(
        "GET",
        `api/order-settings?${params.toString()}`
      );
      return res.data ?? [];
    },
    enabled: !!user?.userId && !!user?.role,
  });

  const { data: runningBots } = useQuery<AutoTradeRunningStatusResponse>({
    queryKey: ["api/auto-trade-running-status"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<AutoTradeRunningStatusResponse>>(
        "GET",
        "api/auto-trade-running-status",
        undefined,
        undefined,
        "crypto"
      );
      return res.data;
    },
    enabled: !!user?.userId,
  });

  const { data: cutoffStatus, isLoading: cutoffStatusLoading } = useQuery<CutoffStatusResponse | null>({
    queryKey: ["api/auto-cutoff-status", user?.userId],
    queryFn: async () => {
      if (!user?.userId) return null;
      const res = await apiRequest<ApiResponse<CutoffStatusResponse>>(
        "GET",
        `api/auto-cutoff/status/${user.userId}`,
        undefined,
        undefined,
        "crypto"
      );
      return res.data;
    },
    enabled: !!user?.userId,
  });

  // ── Mutations ────────────────────────────────────────────────────────────

  const invalidateStrategies = () => {
    queryClient.invalidateQueries({ queryKey: ["api/master-strategies"] });
    queryClient.invalidateQueries({ queryKey: ["api/copy-trading"] });
  };

  const saveStrategyMutation = useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: StrategyFormData }) => {
      const payload = toPayload(values);
      return id
        ? apiRequest("PUT", `api/master-strategies/${id}`, payload, undefined, "crypto")
        : apiRequest("POST", "api/master-strategies", payload, undefined, "crypto");
    },
    onSuccess: (_, variables) => {
      invalidateStrategies();
      setShowCreate(false);
      setEditingStrategy(null);
      strategyForm.reset(defaultStrategyValues);
      toast({
        title: "Success",
        description: variables.id ? "Strategy updated" : "Strategy created",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Save failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const deleteStrategyMutation = useMutation({
    mutationFn: async (id: string) =>
      apiRequest("DELETE", `api/master-strategies/${id}`, undefined, undefined, "crypto"),
    onSuccess: () => {
      invalidateStrategies();
      toast({ title: "Success", description: "Strategy deactivated" });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiRequest(
        "PUT",
        `api/master-strategies/${id}`,
        { isActive },
        undefined,
        "crypto"
      ),
    onSuccess: () => {
      invalidateStrategies();
      toast({ title: "Success", description: "Strategy status updated" });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const subscribeMutation = useMutation({
    mutationFn: async (values: SubscribeFormValues) =>
      apiRequest(
        "POST",
        "api/copy-trading/subscribe",
        { masterStrategyId: subscribeTarget?._id, ...toSubscriptionPayload(values) },
        undefined,
        "crypto"
      ),
    onSuccess: () => {
      invalidateStrategies();
      setSubscribeTarget(null);
      subscribeForm.reset(emptySubscribe);
      toast({ title: "Subscribed", description: "You are now copying this strategy" });
    },
    onError: (error: any) => {
      toast({
        title: "Subscription failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: async (values: SubscriptionUpdateValues) =>
      apiRequest(
        "PUT",
        `api/copy-trading/subscription/${editSubscription?.masterStrategyId}`,
        { isEnabled: values.isEnabled, ...toSubscriptionPayload(values) },
        undefined,
        "crypto"
      ),
    onSuccess: () => {
      invalidateStrategies();
      setEditSubscription(null);
      toast({ title: "Success", description: "Subscription settings updated" });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async (masterId: string) =>
      apiRequest(
        "DELETE",
        `api/copy-trading/unsubscribe/${masterId}`,
        undefined,
        undefined,
        "crypto"
      ),
    onSuccess: () => {
      invalidateStrategies();
      toast({ title: "Unsubscribed", description: "No longer copying this strategy" });
    },
    onError: (error: any) => {
      toast({
        title: "Unsubscribe failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const toggleCutoffMutation = useMutation({
    mutationFn: async ({ enabled }: { enabled: boolean }) => {
      if (!user?.userId) throw new Error("User ID not found");
      return apiRequest(
        "POST",
        enabled ? "api/auto-cutoff/start" : "api/auto-cutoff/stop",
        { userId: user.userId },
        undefined,
        "crypto"
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/auto-cutoff-status"] });
      toast({ title: "Success", description: "Auto-cutoff status updated" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update auto cutoff", variant: "destructive" });
    },
  });

  // ── Helpers ──────────────────────────────────────────────────────────────

  const toggleExpansion = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openCreate = () => {
    setEditingStrategy(null);
    strategyForm.reset(defaultStrategyValues);
    setShowCreate(true);
  };

  const openEdit = (strategy: MasterStrategy) => {
    setEditingStrategy(strategy);
    strategyForm.reset(toFormValues(strategy));
    setShowCreate(true);
  };

  const openSubscribe = (strategy: MasterStrategy) => {
    setSubscribeTarget(strategy);
    subscribeForm.reset(emptySubscribe);
  };

  const openEditSubscription = (subscription: CopyTradingSubscription) => {
    setEditSubscription(subscription);
    subscriptionForm.reset({
      isEnabled: subscription.isEnabled,
      capitalAllocation: subscription.capitalAllocation ?? 100,
      maxPositionSize: percentToForm(subscription.maxPositionSize),
      stopLossOverride: percentToForm(subscription.stopLossOverride),
      trailingStopOverride: percentToForm(subscription.trailingStopOverride),
    });
  };

  const subscribedIds = new Set(subscriptions?.map((s) => s.masterStrategyId) ?? []);

  const isLoading = strategiesLoading || subscriptionsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Auto Trade</h1>
              <p className="text-gray-600 mt-1">
                Create strategies, run them, and let other users copy them.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    runningBots?.running_bot_ids?.length
                      ? "bg-green-500 animate-pulse"
                      : "bg-red-500"
                  }`}
                ></div>
                <span className="text-sm font-medium">
                  {runningBots?.running_bot_ids?.length ? "System Active" : "System Inactive"}
                </span>
              </div>

              <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Auto-Cutoff:</span>
                <Checkbox
                  checked={cutoffStatus?.cutoff_running ?? false}
                  onCheckedChange={(checked) =>
                    toggleCutoffMutation.mutate({ enabled: checked === true })
                  }
                  disabled={cutoffStatusLoading || toggleCutoffMutation.isPending}
                />
                <Badge
                  variant={cutoffStatus?.cutoff_running ? "default" : "secondary"}
                  className="text-xs"
                >
                  {cutoffStatus?.cutoff_running ? "Active" : "Inactive"}
                </Badge>
              </div>

              {isAdmin && (
                <Button onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-2" /> New Strategy
                </Button>
              )}
            </div>
          </div>

          {currentTradingMode !== CRYPTO && (
            <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Strategies run on the crypto backend. Switch to crypto mode to manage the rest of the
              app.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="strategies">Strategies</TabsTrigger>
          <TabsTrigger value="subscriptions">
            My Subscriptions ({subscriptions?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        {/* ── Strategies ─────────────────────────────────────────────── */}
        <TabsContent value="strategies">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5" /> Strategies
              </CardTitle>
              <CardDescription>
                {isAdmin
                  ? "Create and activate strategies. Other users subscribe to them and the shared trading cycle executes them."
                  : "Browse strategies and subscribe to the ones you want to copy."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Strategy</th>
                      <th className="px-3 py-2 font-medium">Refresh</th>
                      <th className="px-3 py-2 font-medium">Filters</th>
                      <th className="px-3 py-2 font-medium">Subscribers</th>
                      <th className="px-3 py-2 font-medium">Options</th>
                      {isAdmin && <th className="px-3 py-2 font-medium">Active</th>}
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!strategies?.length ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-muted-foreground">
                          {isAdmin
                            ? "No strategies yet. Create one to get started."
                            : "No strategies available yet."}
                        </td>
                      </tr>
                    ) : (
                      strategies.map((strategy) => {
                        const isExpanded = expanded.has(strategy._id);
                        const isSubscribed = subscribedIds.has(strategy._id);
                        return (
                          <StrategyRow
                            key={strategy._id}
                            strategy={strategy}
                            isExpanded={isExpanded}
                            isSubscribed={isSubscribed}
                            isAdmin={isAdmin}
                            filters={filters}
                            buckets={buckets}
                            orderSettings={orderSettings}
                            onToggleExpand={() => toggleExpansion(strategy._id)}
                            onEdit={() => openEdit(strategy)}
                            onToggleActive={() =>
                              toggleActiveMutation.mutate({
                                id: strategy._id,
                                isActive: !strategy.isActive,
                              })
                            }
                            onDelete={() => deleteStrategyMutation.mutate(strategy._id)}
                            onSubscribe={() => openSubscribe(strategy)}
                            isMutating={
                              toggleActiveMutation.isPending || deleteStrategyMutation.isPending
                            }
                          />
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── My Subscriptions ──────────────────────────────────────── */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5" /> My Subscriptions
                </span>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("strategies")}>
                  Browse Strategies
                </Button>
              </CardTitle>
              <CardDescription>
                Control how much capital each strategy may use and whether it is currently copying
                for you.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Strategy</th>
                      <th className="px-3 py-2 font-medium">Allocation</th>
                      <th className="px-3 py-2 font-medium">Overrides</th>
                      <th className="px-3 py-2 font-medium">Trades</th>
                      <th className="px-3 py-2 font-medium">PnL</th>
                      <th className="px-3 py-2 font-medium">Enabled</th>
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!subscriptions?.length ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-muted-foreground">
                          You are not subscribed to any strategy.
                        </td>
                      </tr>
                    ) : (
                      subscriptions.map((subscription) => (
                        <tr key={subscription._id} className="border-t hover:bg-muted/50">
                          <td className="px-3 py-2">
                            <div className="font-medium">
                              {subscription.master_strategy_name || subscription.masterStrategyId}
                            </div>
                            {subscription.master_strategy_active === false && (
                              <Badge variant="secondary" className="mt-1">
                                Strategy inactive
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2">{subscription.capitalAllocation}%</td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {[
                              subscription.maxPositionSize != null &&
                                `max ${subscription.maxPositionSize}`,
                              subscription.stopLossOverride != null &&
                                `sl ${subscription.stopLossOverride}%`,
                              subscription.trailingStopOverride != null &&
                                `trail ${subscription.trailingStopOverride}%`,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </td>
                          <td className="px-3 py-2">{subscription.totalTrades}</td>
                          <td
                            className={`px-3 py-2 ${
                              subscription.totalPnl > 0
                                ? "text-green-600"
                                : subscription.totalPnl < 0
                                ? "text-red-600"
                                : ""
                            }`}
                          >
                            {subscription.totalPnl}
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              className={
                                subscription.isEnabled && subscription.isActive
                                  ? "bg-green-100 text-green-700 hover:bg-green-100"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-100"
                              }
                            >
                              {subscription.isEnabled && subscription.isActive ? "On" : "Off"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditSubscription(subscription)}
                                aria-label="Settings"
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={unsubscribeMutation.isPending}
                                onClick={() =>
                                  unsubscribeMutation.mutate(subscription.masterStrategyId)
                                }
                                aria-label="Unsubscribe"
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* System status + emergency controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" /> Live Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    runningBots?.running_bot_ids?.length ? "bg-green-500 animate-pulse" : "bg-red-500"
                  }`}
                ></div>
                <div>
                  <p className="font-medium text-gray-900">System Status</p>
                  <p className="text-sm text-gray-600">
                    {runningBots?.running_bot_ids?.length || 0} active strategies
                  </p>
                </div>
              </div>
              <Badge
                className={`px-3 py-1 ${
                  runningBots?.running_bot_ids?.length
                    ? "bg-green-100 text-green-800 border-green-200"
                    : "bg-red-100 text-red-800 border-red-200"
                }`}
              >
                {runningBots?.running_bot_ids?.length ? "Active" : "Inactive"}
              </Badge>
            </div>

            {runningBots?.details?.length ? (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 text-sm">Running Strategies:</h4>
                {runningBots.details.map((bot) => (
                  <div key={bot.bot_id} className="flex items-center justify-between p-3 bg-white rounded border">
                    <span className="text-sm font-medium text-gray-900">{bot.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{bot.refresh_interval}s</span>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No strategies are currently running.</p>
            )}
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-900 flex items-center gap-2">
                <Power className="w-5 h-5" />
                Emergency Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full border-orange-300 text-orange-700 hover:bg-orange-100 hover:border-orange-400 flex items-center gap-2"
                onClick={() => {
                  strategies
                    ?.filter((s) => s.isActive)
                    .forEach((s) => toggleActiveMutation.mutate({ id: s._id, isActive: false }));
                  toast({ title: "All Strategies Paused", description: "You can reactivate them individually" });
                }}
                disabled={!strategies?.some((s) => s.isActive)}
              >
                <Pause className="w-4 h-4" />
                Pause All Strategies
              </Button>

              <Button
                variant="outline"
                className="w-full border-gray-400 text-gray-700 hover:bg-gray-100 hover:border-gray-500 flex items-center gap-2"
                onClick={() =>
                  toast({ title: "Position Closure", description: "All open positions will be closed" })
                }
              >
                <RotateCcw className="w-4 h-4" />
                Close All Positions
              </Button>

              <div className="text-xs text-red-600 mt-2 p-2 bg-red-100 rounded border border-red-200">
                <strong>Warning:</strong> Use emergency controls only when necessary. These actions
                cannot be undone.
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{strategies?.length ?? 0}</div>
            <div className="text-sm text-blue-600">Total Strategies</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-700">
              {strategies?.filter((s) => s.isActive).length ?? 0}
            </div>
            <div className="text-sm text-green-600">Active Strategies</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-700">
              {strategies?.reduce((sum, s) => sum + (s.subscriber_count ?? 0), 0) ?? 0}
            </div>
            <div className="text-sm text-purple-600">Total Subscribers</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-700">{subscriptions?.length ?? 0}</div>
            <div className="text-sm text-orange-600">Your Subscriptions</div>
          </CardContent>
        </Card>
      </div>

      {/* ── Create / Edit strategy dialog ─────────────────────────────── */}
      <Dialog
        open={showCreate}
        onOpenChange={(open) => {
          setShowCreate(open);
          if (!open) {
            setEditingStrategy(null);
            strategyForm.reset(defaultStrategyValues);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingStrategy ? "Edit Strategy" : "New Strategy"}</DialogTitle>
            <DialogDescription>
              {editingStrategy
                ? "Update the strategy. Changes apply to all current and future subscribers."
                : "Create a strategy that other users can subscribe to."}
            </DialogDescription>
          </DialogHeader>

          <Form {...strategyForm}>
            <form
              onSubmit={strategyForm.handleSubmit((values) =>
                saveStrategyMutation.mutate({ id: editingStrategy?._id, values })
              )}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={strategyForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Crypto Momentum" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={strategyForm.control}
                  name="refreshInterval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Refresh interval (seconds)</FormLabel>
                      <FormControl>
                        <Input type="number" min={5} max={86400} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={strategyForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={2}
                          placeholder="What does this strategy trade?"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={strategyForm.control}
                  name="maxFollowers"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max followers (blank = unlimited)</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} placeholder="Unlimited" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={strategyForm.control}
                  name="minCapital"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum capital (blank = none)</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} step="any" placeholder="None" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(
                  [
                    ["autoCutoffEnabled", "Auto cutoff"],
                    ["trailingEnabled", "Trailing stop"],
                    ["isActive", "Active"],
                  ] as const
                ).map(([name, label]) => (
                  <FormField
                    key={name}
                    control={strategyForm.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between border rounded-lg px-3 py-2">
                        <FormLabel className="text-sm text-gray-700 mb-0">{label}</FormLabel>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-semibold text-gray-700">
                    Filter configurations
                  </FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      append({
                        filterId: "",
                        bucketIds: [],
                        orderSettingId: "",
                        timeInMinutes: 60,
                        exitFilterIds: [],
                      })
                    }
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add config
                  </Button>
                </div>

                <FilterConfigurationForm
                  form={strategyForm}
                  fields={fields}
                  append={append}
                  remove={remove}
                  buckets={buckets}
                  orderSettings={orderSettings}
                  filters={filters}
                />
                {strategyForm.formState.errors.filterConfigs?.message && (
                  <p className="text-sm text-red-500">
                    {strategyForm.formState.errors.filterConfigs.message}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false);
                    setEditingStrategy(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saveStrategyMutation.isPending}>
                  {saveStrategyMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
                    </>
                  ) : editingStrategy ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* ── Subscribe dialog ──────────────────────────────────────────── */}
      <Dialog
        open={!!subscribeTarget}
        onOpenChange={(open) => {
          if (!open) setSubscribeTarget(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Subscribe to {subscribeTarget?.name}</DialogTitle>
            <DialogDescription>
              Choose how much of your capital this strategy may use.
            </DialogDescription>
          </DialogHeader>
          <Form {...subscribeForm}>
            <form
              onSubmit={subscribeForm.handleSubmit((values) => subscribeMutation.mutate(values))}
              className="space-y-4"
            >
              <FormField
                control={subscribeForm.control}
                name="capitalAllocation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Capital allocation (%)</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={100} {...field} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">Share of your capital, 1 – 100</p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <SubscriptionOverrideFields control={subscribeForm.control} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSubscribeTarget(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={subscribeMutation.isPending}>
                  {subscribeMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subscribing...
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* ── Subscription settings dialog ─────────────────────────────── */}
      <Dialog
        open={!!editSubscription}
        onOpenChange={(open) => {
          if (!open) setEditSubscription(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Settings for {editSubscription?.master_strategy_name || editSubscription?.masterStrategyId}
            </DialogTitle>
            <DialogDescription>
              Overrides apply only to your subscription; the strategy itself is unchanged.
            </DialogDescription>
          </DialogHeader>
          <Form {...subscriptionForm}>
            <form
              onSubmit={subscriptionForm.handleSubmit((values) =>
                updateSubscriptionMutation.mutate(values)
              )}
              className="space-y-4"
            >
              <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                <span className="text-sm font-medium">Copy trades</span>
                <Switch
                  checked={subscriptionForm.watch("isEnabled")}
                  onCheckedChange={(checked) =>
                    subscriptionForm.setValue("isEnabled", checked)
                  }
                />
              </div>
              <SubscriptionOverrideFields control={subscriptionForm.control} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditSubscription(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateSubscriptionMutation.isPending}>
                  {updateSubscriptionMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Strategy row ───────────────────────────────────────────────────────────

function StrategyRow({
  strategy,
  isExpanded,
  isSubscribed,
  isAdmin,
  filters,
  buckets,
  orderSettings,
  onToggleExpand,
  onEdit,
  onToggleActive,
  onDelete,
  onSubscribe,
  isMutating,
}: {
  strategy: MasterStrategy;
  isExpanded: boolean;
  isSubscribed: boolean;
  isAdmin: boolean;
  filters?: FilterApiSchema[];
  buckets?: Bucket[];
  orderSettings?: OrderSettingsFormData[];
  onToggleExpand: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onSubscribe: () => void;
  isMutating: boolean;
}) {
  return (
    <>
      <tr className="border-t hover:bg-muted/50 align-top">
        <td className="px-3 py-2">
          <div className="font-medium">{strategy.name}</div>
          {strategy.description && (
            <div className="text-xs text-gray-500 truncate max-w-xs">{strategy.description}</div>
          )}
        </td>
        <td className="px-3 py-2">{strategy.refreshInterval}s</td>
        <td className="px-3 py-2">{strategy.filterConfigs?.length ?? 0}</td>
        <td className="px-3 py-2">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3 h-3" />
            {strategy.subscriber_count ?? 0}
            {strategy.maxFollowers != null ? ` / ${strategy.maxFollowers}` : ""}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className="text-xs">
              {strategy.autoCutoffEnabled ? "Cutoff" : "Manual"}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {strategy.trailingEnabled ? "Trailing" : "No trailing"}
            </Badge>
          </div>
        </td>
        {isAdmin && (
          <td className="px-3 py-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={strategy.isActive}
                disabled={isMutating}
                onCheckedChange={onToggleActive}
              />
              <Badge
                className={
                  strategy.isActive
                    ? "bg-green-100 text-green-700 hover:bg-green-100"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-100"
                }
              >
                {strategy.isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          </td>
        )}
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleExpand}
              aria-label="Toggle details"
              className="h-8 w-8 p-0"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>

            {isAdmin ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onEdit}
                  aria-label="Edit"
                  className="h-8 w-8 p-0"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isMutating}
                  onClick={onDelete}
                  aria-label="Deactivate"
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                disabled={isSubscribed || !strategy.isActive}
                onClick={onSubscribe}
                className="h-8 text-xs"
              >
                {isSubscribed ? (
                  <>
                    <BadgeCheck className="w-3 h-3 mr-1" /> Subscribed
                  </>
                ) : (
                  "Subscribe"
                )}
              </Button>
            )}
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr className="border-t bg-gray-50/50">
          <td colSpan={isAdmin ? 7 : 6} className="px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900 text-sm">Filter Configurations</h4>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {strategy.filterConfigs.length} filter(s)
              </span>
            </div>
            <div className="space-y-2">
              {strategy.filterConfigs.map((config, index) => {
                const filter = filters?.find((f) => f.id === config.filterId);
                const orderSetting = orderSettings?.find((o) => o.id === config.orderSettingId);
                const selectedBuckets = buckets?.filter((b) => config.bucketIds.includes(b._id));
                return (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 border border-gray-200 rounded-md bg-white"
                  >
                    <div className="flex items-center gap-2 min-w-[150px]">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded border">
                        #{index + 1}
                      </span>
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {filter?.function_name || "Unknown Filter"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-700">
                      <span>
                        <span className="font-medium">Setting:</span> {orderSetting?.name || "—"}
                      </span>
                      <span>
                        <span className="font-medium">Cutoff:</span> {config.timeInMinutes}m
                      </span>
                    </div>
                    {selectedBuckets?.length ? (
                      <div className="flex items-center gap-1 flex-wrap text-xs">
                        <span className="font-medium text-gray-600">Buckets:</span>
                        {selectedBuckets.slice(0, 3).map((bucket) => (
                          <span
                            key={bucket._id}
                            className="inline-block bg-gray-100 border border-gray-300 rounded px-2 py-0.5 truncate max-w-[100px]"
                            title={bucket.name}
                          >
                            {bucket.name}
                          </span>
                        ))}
                        {selectedBuckets.length > 3 && (
                          <span className="text-gray-500">+{selectedBuckets.length - 3}</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Subscription override fields ───────────────────────────────────────────

const overrideFields: {
  name: "maxPositionSize" | "stopLossOverride" | "trailingStopOverride";
  label: string;
  hint: string;
  max?: number;
}[] = [
  { name: "maxPositionSize", label: "Max position size", hint: "Optional, > 0" },
  { name: "stopLossOverride", label: "Stop loss override (%)", hint: "0 – 100", max: 100 },
  { name: "trailingStopOverride", label: "Trailing stop override (%)", hint: "0 – 100", max: 100 },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SubscriptionOverrideFields({ control }: { control: any }) {
  return (
    <>
      {overrideFields.map((item) => (
        <FormField
          key={item.name}
          control={control}
          name={item.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{item.label}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  min={0}
                  max={item.max}
                  placeholder="Not set"
                  {...field}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">{item.hint}</p>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
}
